import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';

// ============================================================================
// Types
// ============================================================================

type ThemeMode = 'light' | 'dark';
// Explicit marker for "user intentionally chose light/dark".
// We keep this separate from the `theme` value because `theme` is also written
// during bootstrap, which should not count as a user override.
export const THEME_MODE_USER_OVERRIDE_STORAGE_KEY = 'themeModeUserOverride';

interface ThemeJson {
  name?: string;
  displayName?: string;
  description?: string;
  version?: string;
  light?: Record<string, string>;
  dark?: Record<string, string>;
}

interface ThemeMetadata {
  id: string;
  name: string;
  description: string;
  hasLight: boolean;
  hasDark: boolean;
}

interface ThemeRegistry {
  themes: ThemeMetadata[];
}

interface ClientMapping {
  clientThemeMapping: Record<string, string>;
  fallbackTheme: string;
}

// ============================================================================
// Service
// ============================================================================

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  // Observable state for components to subscribe to
  private darkModeSubject = new BehaviorSubject<boolean>(false);
  public darkMode$: Observable<boolean> = this.darkModeSubject.asObservable();

  // Explicit readiness state so components don't need to infer theme initialization.
  private themeReadySubject = new BehaviorSubject<boolean>(false);
  public themeReady$: Observable<boolean> = this.themeReadySubject.asObservable();

  // Observable state for current theme
  private currentThemeSubject = new BehaviorSubject<string>('default');
  public currentTheme$: Observable<string> = this.currentThemeSubject.asObservable();

  // Observable state for available themes
  private availableThemesSubject = new BehaviorSubject<ThemeMetadata[]>([]);
  public availableThemes$: Observable<ThemeMetadata[]> = this.availableThemesSubject.asObservable();

  // Cache for themes
  private themeRegistry: ThemeRegistry | null = null;
  private clientMapping: ClientMapping | null = null;
  private loadedThemes: Map<string, ThemeJson> = new Map();
  private appliedThemeVariableNames: Set<string> = new Set();

  constructor(
    @Inject(DOCUMENT) private document: Document
  ) {}

  // ============================================================================
  // Public API
  // ============================================================================

  /**
   * Initialize theme on app startup
   */
  async initTheme(): Promise<void> {
    this.themeReadySubject.next(false);

    // Load theme metadata
    this.themeRegistry = await this.loadThemeRegistry();
    this.clientMapping = await this.loadClientMapping();

    // Load local themes from sessionStorage
    this.loadLocalThemesFromStorage();

    this.availableThemesSubject.next(this.themeRegistry.themes);

    // Resolve client ID and theme
    const clientId = this.resolveClientId();
    const themeId = this.resolveThemeId(clientId);
    const mode = this.resolveThemeMode();

    // Apply mode immediately
    this.setDarkMode(mode === 'dark');

    // Load and apply theme
    await this.loadAndApplyTheme(themeId, mode);
  }

  /**
   * Toggle between light and dark mode
   */
  toggleTheme(): void {
    this.setDarkMode(!this.darkModeSubject.value);
  }

  /**
   * Set theme mode explicitly
   */
  setDarkMode(isDark: boolean): void {
    this.darkModeSubject.next(isDark);
    this.applyDarkModeClass(isDark);
    this.persistThemeMode(isDark ? 'dark' : 'light');

    // Re-apply CSS variables and font-family for current theme
    const currentTheme = this.currentThemeSubject.value;
    const themeJson = this.loadedThemes.get(currentTheme);
    if (themeJson) {
      const mode = isDark ? 'dark' : 'light';
      this.applyThemeVariables(themeJson, mode);
      this.applyThemeFontFamily(themeJson, mode);
    }
  }

  setDarkModeFromUser(isDark: boolean): void {
    // Persist explicit user intent before applying mode.
    this.markThemeModeUserOverride();
    this.setDarkMode(isDark);
  }

  toggleThemeFromUser(): void {
    // Same semantics as setDarkModeFromUser for toggle-based UIs.
    this.markThemeModeUserOverride();
    this.toggleTheme();
  }

  hasThemeModeUserOverride(): boolean {
    try {
      return sessionStorage.getItem(THEME_MODE_USER_OVERRIDE_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  /**
   * Set theme by ID
   */
  async setTheme(themeId: string): Promise<void> {
    this.themeReadySubject.next(false);

    // Validate theme exists
    const themeExists = this.themeRegistry?.themes.some(t => t.id === themeId);
    if (!themeExists) {
      console.warn(`Theme '${themeId}' not found in registry`);
      this.themeReadySubject.next(true);
      return;
    }

    // Save user's theme preference
    sessionStorage.setItem('selected_theme', themeId);
    this.currentThemeSubject.next(themeId);

    // Load and apply theme
    const mode = this.getCurrentTheme();
    await this.loadAndApplyTheme(themeId, mode);
  }

  /**
   * Resolve a theme ID to a valid registered theme.
   * - Trims/normalizes input
   * - Returns fallback when missing/invalid
   */
  resolveValidThemeId(themeId: string | null | undefined, fallbackThemeId: string = 'zen'): string {
    const requested = (themeId ?? '').trim();
    if (!requested) return fallbackThemeId;

    const themeExists = this.themeRegistry?.themes.some(t => t.id === requested);
    return themeExists ? requested : fallbackThemeId;
  }

  /**
   * Apply a theme if it's valid; otherwise apply fallback.
   * This does NOT persist to sessionStorage unless the theme is valid.
   */
  async setThemeOrFallback(themeId: string | null | undefined, fallbackThemeId: string = 'zen'): Promise<void> {
    const resolved = this.resolveValidThemeId(themeId, fallbackThemeId);

    // If registry isn't loaded yet, fall back to existing setTheme behavior
    // (which also validates once registry becomes available).
    if (!this.themeRegistry) {
      await this.setTheme(resolved);
      return;
    }

    // Only persist if the requested theme was actually valid.
    const requested = (themeId ?? '').trim();
    const requestedValid = !!requested && resolved === requested;

    if (requestedValid) {
      await this.setTheme(resolved);
      return;
    }

    // Apply fallback without persisting selection.
    const mode = this.getCurrentTheme();
    await this.loadAndApplyTheme(resolved, mode);
  }

  /**
   * Restore persisted browser mode/theme when present, otherwise apply the provided fallback theme.
   * Theme precedence:
   * 1) saved `selected_theme`
   * 2) provided preferred theme id
   * 3) fallback theme id
   */
  async restorePersistedThemeOrApply(preferredThemeId: string | null | undefined, fallbackThemeId: string = 'zen'): Promise<void> {
    const savedMode = sessionStorage.getItem('theme');
    if (savedMode === 'dark' || savedMode === 'light') {
      this.setDarkMode(savedMode === 'dark');
    }

    const savedThemeId = sessionStorage.getItem('selected_theme');
    await this.setThemeOrFallback(savedThemeId || preferredThemeId, fallbackThemeId);
  }

  /**
   * Check if dark mode is active
   */
  isDarkMode(): boolean {
    return this.darkModeSubject.value;
  }

  /**
   * Get current theme mode
   */
  getCurrentTheme(): ThemeMode {
    return this.darkModeSubject.value ? 'dark' : 'light';
  }

  /**
   * Get current theme ID
   */
  getCurrentThemeId(): string {
    return this.currentThemeSubject.value;
  }

  /**
   * Get available themes
   */
  getAvailableThemes(): ThemeMetadata[] {
    return this.availableThemesSubject.value;
  }

  /**
   * Get current client ID (for backward compatibility)
   */
  getCurrentClient(): string {
    return sessionStorage.getItem('client') || 'default';
  }

  isThemeReady(): boolean {
    return this.themeReadySubject.value;
  }

  /**
   * Diagnostic helper: returns the list of CSS custom properties currently applied by ThemeService.
   * Useful for debugging theme overlap / stale variable issues.
   */
  public getAppliedThemeVariables(): string[] {
    return Array.from(this.appliedThemeVariableNames);
  }

  // ============================================================================
  // Private Methods - Initialization
  // ============================================================================

  private async loadThemeRegistry(): Promise<ThemeRegistry> {
    try {
      const baseUrl = this.document.baseURI || '/';
      const url = new URL('assets/themes/theme-registry.json', baseUrl).toString();
      const response = await fetch(url, { cache: 'no-cache' });

      if (!response.ok) {
        throw new Error('Failed to load theme registry');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to load theme registry', error);
      return { themes: [] };
    }
  }

  private async loadClientMapping(): Promise<ClientMapping> {
    try {
      const baseUrl = this.document.baseURI || '/';
      const url = new URL('assets/themes/client-mapping.json', baseUrl).toString();
      const response = await fetch(url, { cache: 'no-cache' });

      if (!response.ok) {
        throw new Error('Failed to load client mapping');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to load client mapping', error);
      return { clientThemeMapping: {}, fallbackTheme: 'default' };
    }
  }

  private async loadThemeJson(themeId: string): Promise<ThemeJson> {
    // Check cache first
    if (this.loadedThemes.has(themeId)) {
      return this.loadedThemes.get(themeId)!;
    }

    try {
      const baseUrl = this.document.baseURI || '/';
      const url = new URL(`assets/themes/${themeId}.json`, baseUrl).toString();
      const response = await fetch(url, { cache: 'no-cache' });

      if (!response.ok) {
        throw new Error(`Failed to load theme: ${themeId}`);
      }

      const themeJson = await response.json();
      this.loadedThemes.set(themeId, themeJson);
      return themeJson;
    } catch (error) {
      console.warn(`Theme '${themeId}' not found, using empty theme`, error);
      const emptyTheme = { light: {}, dark: {} };
      this.loadedThemes.set(themeId, emptyTheme);
      return emptyTheme;
    }
  }

  private async loadAndApplyTheme(themeId: string, mode: ThemeMode): Promise<void> {
    const themeJson = await this.loadThemeJson(themeId);
    this.applyThemeVariables(themeJson, mode);
    this.applyDataStyleAttribute(themeId);
    this.applyThemeFontFamily(themeJson, mode);
    this.currentThemeSubject.next(themeId);
    this.themeReadySubject.next(true);
  }

  private resolveClientId(): string {
    // Check URL query parameter first
    const urlClient = this.getQueryParam('client');
    if (urlClient) {
      sessionStorage.setItem('client', urlClient);
      return urlClient;
    }

    // Fall back to sessionStorage or default
    return sessionStorage.getItem('client') || 'default';
  }

  private resolveThemeId(clientId: string): string {
    // Check if user has overridden theme selection
    const selectedTheme = sessionStorage.getItem('selected_theme');
    if (selectedTheme) {
      return selectedTheme;
    }

    // Get default theme for client
    return this.getDefaultThemeForClient(clientId);
  }

  private getDefaultThemeForClient(clientId: string): string {
    if (!this.clientMapping) {
      return 'default';
    }

    return this.clientMapping.clientThemeMapping[clientId] || this.clientMapping.fallbackTheme;
  }

  private resolveThemeMode(): ThemeMode {
    const saved = sessionStorage.getItem('theme');
    if (saved === 'dark' || saved === 'light') {
      return saved;
    }

    // Fall back to dark mode by default
    try {
      const prefersDark = this.document.defaultView
        ?.matchMedia?.('(prefers-color-scheme: dark)')
        ?.matches;
      return prefersDark ? 'dark' : 'dark';
    } catch {
      return 'dark';
    }
  }

  // ============================================================================
  // Private Methods - Application
  // ============================================================================

  private applyThemeVariables(theme: ThemeJson, mode: ThemeMode): void {
    const vars = theme[mode] || {};
    this.clearPreviouslyAppliedThemeVariables();
    this.setCssVariables(vars);
    this.appliedThemeVariableNames = new Set(
      Object.keys(vars).filter(name => name.startsWith('--'))
    );
  }

  private applyDarkModeClass(isDark: boolean): void {
    // Apply class to body for backward compatibility
    if (isDark) {
      this.document.body.classList.add('dark-theme');
    } else {
      this.document.body.classList.remove('dark-theme');
    }

    // Apply data-theme attribute to html element for test-components compatibility
    const htmlElement = this.document.documentElement;
    htmlElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }

  private applyDataStyleAttribute(themeId: string): void {
    // Apply data-style attribute to html element for test-components compatibility
    const htmlElement = this.document.documentElement;
    htmlElement.setAttribute('data-style', themeId);
  }

  private applyThemeFontFamily(theme: ThemeJson, mode: ThemeMode): void {
    // Apply font-family to body if theme defines it (e.g., Custom)
    const vars = theme[mode] || {};
    const fontFamily = vars['--font-family'];

    if (fontFamily) {
      this.document.body.style.fontFamily = fontFamily;
    } else {
      // Reset to default if no font-family specified
      this.document.body.style.fontFamily = '';
    }
  }

  private persistThemeMode(mode: ThemeMode): void {
    sessionStorage.setItem('theme', mode);
  }

  private markThemeModeUserOverride(): void {
    try {
      sessionStorage.setItem(THEME_MODE_USER_OVERRIDE_STORAGE_KEY, 'true');
    } catch {
      // ignore (some environments block storage writes)
    }
  }

  private clearPreviouslyAppliedThemeVariables(): void {
    const root = this.document.documentElement;
    const body = this.document.body;

    this.appliedThemeVariableNames.forEach(name => {
      root.style.removeProperty(name);
      body.style.removeProperty(name);
    });
  }

  private setCssVariables(vars: Record<string, string>): void {
    const root = this.document.documentElement;
    const body = this.document.body;

    Object.entries(vars).forEach(([name, value]) => {
      if (name.startsWith('--') && typeof value === 'string') {
        root.style.setProperty(name, value);
        body.style.setProperty(name, value);
      }
    });
  }

  private getQueryParam(name: string): string | null {
    try {
      const search = this.document.defaultView?.location?.search || '';
      const params = new URLSearchParams(search);
      return params.get(name);
    } catch {
      return null;
    }
  }

  // ============================================================================
  // Local Theme Support
  // ============================================================================

  /**
   * Add a local theme to the available themes and cache it
   */
  public addLocalTheme(metadata: ThemeMetadata, themeJson: ThemeJson): void {
    if (!this.themeRegistry) {
      this.themeRegistry = { themes: [] };
    }

    // Remove existing theme with same ID if present
    this.themeRegistry.themes = this.themeRegistry.themes.filter(t => t.id !== metadata.id);

    // Add the new local theme
    this.themeRegistry.themes.push(metadata);

    // Cache the theme JSON
    this.loadedThemes.set(metadata.id, themeJson);

    // Update available themes observable
    this.availableThemesSubject.next(this.themeRegistry.themes);
  }

  /**
   * Load all local themes from sessionStorage on startup
   */
  private loadLocalThemesFromStorage(): void {
    if (!this.themeRegistry) {
      this.themeRegistry = { themes: [] };
    }

    try {
      // Iterate through sessionStorage to find theme entries
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('theme_') && key.endsWith('-local')) {
          const themeId = key.replace('theme_', '');
          const themeJson = sessionStorage.getItem(key);

          if (themeJson) {
            try {
              const parsed = JSON.parse(themeJson) as ThemeJson;
              const themeName = parsed.name || parsed.displayName || themeId.replace('-local', '');

              const metadata: ThemeMetadata = {
                id: themeId,
                name: `${themeName} - local`,
                description: parsed.description || 'Locally uploaded theme',
                hasLight: !!parsed.light,
                hasDark: !!parsed.dark,
              };

              // Add to registry and cache
              this.themeRegistry.themes.push(metadata);
              this.loadedThemes.set(themeId, parsed);
            } catch (e) {
              console.warn(`Failed to parse local theme: ${themeId}`, e);
            }
          }
        }
      }
    } catch (e) {
      console.warn('Failed to load local themes from sessionStorage', e);
    }
  }

  /**
   * Remove a local theme from storage and available themes
   */
  public removeLocalTheme(themeId: string): void {
    if (!themeId.endsWith('-local')) {
      console.warn('Can only remove local themes (ID must end with "-local")');
      return;
    }

    try {
      // Remove from sessionStorage
      sessionStorage.removeItem(`theme_${themeId}`);

      // Remove from registry
      if (this.themeRegistry) {
        this.themeRegistry.themes = this.themeRegistry.themes.filter(t => t.id !== themeId);
        this.availableThemesSubject.next(this.themeRegistry.themes);
      }

      // Remove from cache
      this.loadedThemes.delete(themeId);

      console.log(`Local theme "${themeId}" removed`);
    } catch (e) {
      console.error(`Failed to remove local theme: ${themeId}`, e);
    }
  }
}
